import { API_URL } from '@/constants/env'

import type { PaginatedResponse } from '@/types/Api'
import type {
  EbuyAttachment,
  EbuyNotice,
  EbuyOpportunity,
  EbuyOpportunityActivity,
  EbuyOpportunityDetail,
  EbuyPortalRequest,
  EbuyPortalRequestActionEvent,
  EbuyPortalRequestAmendmentEvent,
  EbuyPortalRequestAmendmentFile,
  EbuyPortalRequestArchiveFile,
  EbuyPortalRequestArtifact,
  EbuyPortalRequestEvent,
  EbuyPortalRequestFile,
  EbuyPortalRequestGeneralFile,
  EbuyPortalRequestQAndAEvent,
  EbuyPortalRequestQAndAFile,
  SharedByUser,
  UsaSpendingAwardSummary,
} from '@/types/ebuy/EbuyEmail'
import type { UUID } from '@/types/Id'

// ─── API response shapes (as returned by the server) ──────────────────────────

interface ApiAttachment {
  id: string
  filename: string
  content_type: string
  size_bytes: number
  presigned_url: string
}

interface ApiNotice {
  id: string
  email_id: string
  notice_type: 'request' | 'quote'
  status: string | null
  vehicle_id: string | null
  request_id: string | null
  quote_id: string | null
  opportunity_id: string | null
  agency_name: string | null
  title: string | null
  description: string | null
  response_deadline: string | null
  posted_at: string | null
  buyer_contact: string | null
  extra_data: Record<string, unknown> | null
  read_at: string | null
  matched_sin: string | null
}

interface ApiSharedByUser {
  id: string
  first_name: string
  last_name: string
  picture_url: string | null
}

type ApiWithStringId<T extends { id: UUID }> = Omit<T, 'id'> & { id: string }

type ApiPortalRequestEvent =
  | ApiWithStringId<EbuyPortalRequestActionEvent>
  | ApiWithStringId<EbuyPortalRequestAmendmentEvent>
  | ApiWithStringId<EbuyPortalRequestQAndAEvent>

type ApiPortalRequestFile =
  | ApiWithStringId<EbuyPortalRequestAmendmentFile>
  | ApiWithStringId<EbuyPortalRequestArchiveFile>
  | ApiWithStringId<EbuyPortalRequestGeneralFile>
  | ApiWithStringId<EbuyPortalRequestQAndAFile>

interface ApiPortalRequestArtifact {
  kind: string
  label: string
  presigned_url: string
}

interface ApiPortalRequest {
  id: string
  schedule_id: string
  schedule_label: string | null
  listed_under_schedule_id: string | null
  listed_under_schedule_label: string | null
  request_id: string
  request_url: string
  active_requests_url: string | null
  captured_at: string | null
  detail_source: string | null
  detail_text: string | null
  page_title: string | null
  row_text: string | null
  link_text: string | null
  has_detail_page: boolean
  has_amendment: boolean
  has_q_and_a: boolean
  is_cancelled: boolean
  requested_schedule_id: string | null
  actual_schedule_id: string | null
  schedule_context_mismatch: boolean
  request_type_code: string | null
  request_type_label: string | null
  request_title: string | null
  reference_number: string | null
  follow_on_requirement: string | null
  previous_rfi_information: string | null
  commercial_status: string | null
  contract_type: string | null
  contact_name: string | null
  contact_organization: string | null
  contact_office: string | null
  contact_email: string | null
  contact_phone: string | null
  agency_name: string | null
  buyer_contact: string | null
  request_issue_at: string | null
  request_close_at: string | null
  time_remaining: string | null
  lead_time: string | null
  delivery_days: string | null
  delivery: string | null
  award_method: string | null
  description: string | null
  description_urls: string[]
  categories: unknown[]
  shipping_addresses: unknown[]
  quote_bid_id: string | null
  attachment_count: number
  general_attachment_count: number
  qa_attachment_count: number
  modification_count: number
  is_gwac: boolean | null
  is_mas: boolean | null
  is_idiq: boolean | null
  is_nst: boolean | null
  is_bpa: boolean | null
  parsed_detail: Record<string, unknown> | null
  rfq_info: Record<string, unknown> | null
  rfq_additional_info: Record<string, unknown> | null
  rfq_props: Record<string, unknown> | null
  events: ApiPortalRequestEvent[]
  files: ApiPortalRequestFile[]
  artifacts: ApiPortalRequestArtifact[]
}

interface ApiOpportunityActivity {
  id: string
  event_kind: string
  event_sequence: number
  activity_at: string | null
  read_at: string | null
  notice_ids: string[]
  portal_request_id: string | null
  portal_event_id: string | null
}

interface ApiOpportunityItem {
  id: string
  request_id: string
  vehicle_id: string | null
  created_pursuit_id: string | null
  archived: boolean
  archived_at: string | null
  archived_by: string | null
  notices: ApiNotice[]
  last_notice_at: string | null
  sins: string[]
  shared_by: ApiSharedByUser | null
  usaspending_award_id: string | null
  usaspending_award_title: string | null
  portal_request?: ApiPortalRequest | null
  activity_count?: number
  unread_activity_count?: number
  latest_unread_activity_at?: string | null
}

interface ApiOpportunityDetailItem extends ApiOpportunityItem {
  emails: Array<{
    id: string
    subject: string | null
    received_at: string | null
    html_content: string | null
    attachments: ApiAttachment[]
    shared_by: ApiSharedByUser | null
  }>
  activities?: ApiOpportunityActivity[]
  usaspending_award: UsaSpendingAwardSummary | null
}

interface ApiOpportunityList {
  count: number
  has_more: boolean
  data: ApiOpportunityItem[]
  focused_page?: number | null
}

interface ApiUnreadCount {
  unread: number
}

interface ApiPursuitCreated {
  pursuit_id: string
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

function mapAttachment(a: ApiAttachment): EbuyAttachment {
  return {
    id: a.id as UUID,
    filename: a.filename,
    presigned_url: a.presigned_url,
    content_type: a.content_type,
  }
}

function mapNotice(n: ApiNotice): EbuyNotice {
  return {
    id: n.id as UUID,
    email_id: n.email_id as UUID,
    notice_type: n.notice_type,
    status: n.status,
    vehicle_id: n.vehicle_id,
    request_id: n.request_id,
    quote_id: n.quote_id,
    opportunity_id: n.opportunity_id as UUID | null,
    agency_name: n.agency_name,
    title: n.title,
    description: n.description,
    response_deadline: n.response_deadline,
    posted_at: n.posted_at,
    buyer_contact: n.buyer_contact,
    additional_data: n.extra_data,
    read_at: n.read_at,
    matched_sin: n.matched_sin,
  }
}

function mapSharedBy(u: ApiSharedByUser): SharedByUser {
  return {
    id: u.id as UUID,
    first_name: u.first_name,
    last_name: u.last_name,
    picture_url: u.picture_url,
  }
}

function mapPortalRequestEvent(
  event: ApiPortalRequestEvent,
): EbuyPortalRequestEvent {
  return {
    ...event,
    id: event.id as UUID,
  }
}

function mapPortalRequestFile(
  file: ApiPortalRequestFile,
): EbuyPortalRequestFile {
  return {
    ...file,
    id: file.id as UUID,
  }
}

function mapPortalRequestArtifact(
  artifact: ApiPortalRequestArtifact,
): EbuyPortalRequestArtifact {
  return {
    kind: artifact.kind,
    label: artifact.label,
    presigned_url: artifact.presigned_url,
  }
}

function mapPortalRequest(portalRequest: ApiPortalRequest): EbuyPortalRequest {
  return {
    id: portalRequest.id as UUID,
    schedule_id: portalRequest.schedule_id,
    schedule_label: portalRequest.schedule_label,
    listed_under_schedule_id: portalRequest.listed_under_schedule_id,
    listed_under_schedule_label: portalRequest.listed_under_schedule_label,
    request_id: portalRequest.request_id,
    request_url: portalRequest.request_url,
    active_requests_url: portalRequest.active_requests_url,
    captured_at: portalRequest.captured_at,
    detail_source: portalRequest.detail_source,
    detail_text: portalRequest.detail_text,
    page_title: portalRequest.page_title,
    row_text: portalRequest.row_text,
    link_text: portalRequest.link_text,
    has_detail_page: portalRequest.has_detail_page,
    has_amendment: portalRequest.has_amendment,
    has_q_and_a: portalRequest.has_q_and_a,
    is_cancelled: portalRequest.is_cancelled,
    requested_schedule_id: portalRequest.requested_schedule_id,
    actual_schedule_id: portalRequest.actual_schedule_id,
    schedule_context_mismatch: portalRequest.schedule_context_mismatch,
    request_type_code: portalRequest.request_type_code,
    request_type_label: portalRequest.request_type_label,
    request_title: portalRequest.request_title,
    reference_number: portalRequest.reference_number,
    follow_on_requirement: portalRequest.follow_on_requirement,
    previous_rfi_information: portalRequest.previous_rfi_information,
    commercial_status: portalRequest.commercial_status,
    contract_type: portalRequest.contract_type,
    contact_name: portalRequest.contact_name,
    contact_organization: portalRequest.contact_organization,
    contact_office: portalRequest.contact_office,
    contact_email: portalRequest.contact_email,
    contact_phone: portalRequest.contact_phone,
    agency_name: portalRequest.agency_name,
    buyer_contact: portalRequest.buyer_contact,
    request_issue_at: portalRequest.request_issue_at,
    request_close_at: portalRequest.request_close_at,
    time_remaining: portalRequest.time_remaining,
    lead_time: portalRequest.lead_time,
    delivery_days: portalRequest.delivery_days,
    delivery: portalRequest.delivery,
    award_method: portalRequest.award_method,
    description: portalRequest.description,
    description_urls: portalRequest.description_urls ?? [],
    categories: portalRequest.categories ?? [],
    shipping_addresses: portalRequest.shipping_addresses ?? [],
    quote_bid_id: portalRequest.quote_bid_id,
    attachment_count: portalRequest.attachment_count,
    general_attachment_count: portalRequest.general_attachment_count,
    qa_attachment_count: portalRequest.qa_attachment_count,
    modification_count: portalRequest.modification_count,
    is_gwac: portalRequest.is_gwac,
    is_mas: portalRequest.is_mas,
    is_idiq: portalRequest.is_idiq,
    is_nst: portalRequest.is_nst,
    is_bpa: portalRequest.is_bpa,
    parsed_detail: portalRequest.parsed_detail,
    rfq_info: portalRequest.rfq_info,
    rfq_additional_info: portalRequest.rfq_additional_info,
    rfq_props: portalRequest.rfq_props,
    events: (portalRequest.events ?? []).map(mapPortalRequestEvent),
    files: (portalRequest.files ?? []).map(mapPortalRequestFile),
    artifacts: (portalRequest.artifacts ?? []).map(mapPortalRequestArtifact),
  }
}

function mapOpportunityActivity(
  item: ApiOpportunityActivity,
): EbuyOpportunityActivity {
  return {
    id: item.id as UUID,
    event_kind: item.event_kind,
    event_sequence: item.event_sequence,
    activity_at: item.activity_at,
    read_at: item.read_at,
    notice_ids: item.notice_ids.map((id) => id as UUID),
    portal_request_id: item.portal_request_id as UUID | null,
    portal_event_id: item.portal_event_id as UUID | null,
  }
}

function mapOpportunity(item: ApiOpportunityItem): EbuyOpportunity {
  return {
    id: item.id as UUID,
    request_id: item.request_id,
    vehicle_id: item.vehicle_id,
    created_pursuit_id: item.created_pursuit_id as UUID | null,
    is_archived: item.archived,
    archived_at: item.archived_at,
    archived_by: item.archived_by as UUID | null,
    notices: item.notices.map(mapNotice),
    last_notice_at: item.last_notice_at,
    sins: item.sins ?? [],
    shared_by: item.shared_by ? mapSharedBy(item.shared_by) : null,
    usaspending_award_id: item.usaspending_award_id ?? null,
    usaspending_award_title: item.usaspending_award_title ?? null,
    portal_request: item.portal_request
      ? mapPortalRequest(item.portal_request)
      : null,
    activity_count: item.activity_count ?? 0,
    unread_activity_count: item.unread_activity_count ?? 0,
    latest_unread_activity_at: item.latest_unread_activity_at ?? null,
  }
}

function mapOpportunityDetail(
  item: ApiOpportunityDetailItem,
): EbuyOpportunityDetail {
  return {
    ...mapOpportunity(item),
    emails: item.emails.map((e) => ({
      id: e.id as UUID,
      subject: e.subject,
      received_at: e.received_at,
      html_content: e.html_content,
      attachments: e.attachments.map(mapAttachment),
      shared_by: e.shared_by ? mapSharedBy(e.shared_by) : null,
    })),
    activities: (item.activities ?? []).map(mapOpportunityActivity),
    usaspending_award: item.usaspending_award ?? null,
  }
}

// ─── Fetch helpers ─────────────────────────────────────────────────────────────

async function checkOk(res: Response): Promise<void> {
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`eBuy API error ${res.status}: ${text || res.statusText}`)
  }
}

function authHeaders(accessToken: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  }
}

// ─── Public fetchers (opportunity-centric) ────────────────────────────────────

export interface EbuyOpportunityQueryParams {
  archived?: boolean
  read_filter?: 'unread' | 'read'
  sort?: 'newest' | 'oldest' | 'due_soonest' | 'recently_updated' | 'agency'
  q?: string
  sin?: string
  limit?: number
  offset?: number
  focus_id?: string
}

export async function fetchEbuyOpportunities(
  orgId: string,
  accessToken: string,
  params: EbuyOpportunityQueryParams = {},
): Promise<PaginatedResponse<EbuyOpportunity>> {
  const url = new URL(
    `${API_URL}/api/v1/organizations/${orgId}/gsa-ebuy/opportunities`,
  )
  const { archived, read_filter, sort, q, sin, limit, offset, focus_id } =
    params
  if (archived !== undefined) url.searchParams.set('archived', String(archived))
  if (read_filter) url.searchParams.set('read_filter', read_filter)
  if (sort) url.searchParams.set('sort', sort)
  if (q) url.searchParams.set('q', q)
  if (sin) url.searchParams.set('sin', sin)
  if (limit !== undefined) url.searchParams.set('limit', String(limit))
  if (offset !== undefined) url.searchParams.set('offset', String(offset))
  if (focus_id) url.searchParams.set('focus_id', focus_id)

  const res = await fetch(url.toString(), { headers: authHeaders(accessToken) })
  await checkOk(res)
  const body = (await res.json()) as ApiOpportunityList
  return {
    count: body.count,
    has_more: body.has_more,
    data: body.data.map(mapOpportunity),
    focused_page: body.focused_page ?? undefined,
  }
}

export async function fetchEbuyOpportunitySearch(
  orgId: string,
  accessToken: string,
  params: Pick<
    EbuyOpportunityQueryParams,
    'archived' | 'read_filter' | 'sort' | 'sin'
  > & { q?: string },
): Promise<EbuyOpportunity[]> {
  // Walk the full paginated opportunity set so client-only structured filters
  // like `vehicle:` can filter before the inbox repaginates locally.
  const allOpportunities: EbuyOpportunity[] = []
  const limit = 100
  let offset = 0
  let hasMore = true

  while (hasMore) {
    const url = new URL(
      `${API_URL}/api/v1/organizations/${orgId}/gsa-ebuy/opportunities`,
    )
    if (params.q) {
      url.searchParams.set('q', params.q)
    }
    url.searchParams.set('limit', String(limit))
    url.searchParams.set('offset', String(offset))
    if (params.read_filter) {
      url.searchParams.set('read_filter', params.read_filter)
    }
    if (params.sort) {
      url.searchParams.set('sort', params.sort)
    }
    if (params.sin) {
      url.searchParams.set('sin', params.sin)
    }
    if (params.archived !== undefined) {
      url.searchParams.set('archived', String(params.archived))
    }

    const res = await fetch(url.toString(), {
      headers: authHeaders(accessToken),
    })
    await checkOk(res)
    const body = (await res.json()) as ApiOpportunityList
    allOpportunities.push(...body.data.map(mapOpportunity))

    hasMore = body.has_more && body.data.length > 0
    offset += body.data.length
  }

  return allOpportunities
}

export async function fetchEbuyOpportunity(
  orgId: string,
  opportunityId: string,
  accessToken: string,
): Promise<EbuyOpportunityDetail> {
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/gsa-ebuy/opportunities/${opportunityId}`,
    { headers: authHeaders(accessToken) },
  )
  await checkOk(res)
  const item = (await res.json()) as ApiOpportunityDetailItem
  return mapOpportunityDetail(item)
}

export async function fetchEbuyOpportunityUnreadCount(
  orgId: string,
  accessToken: string,
): Promise<number> {
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/gsa-ebuy/opportunities/unread-count`,
    { headers: authHeaders(accessToken) },
  )
  await checkOk(res)
  const body = (await res.json()) as ApiUnreadCount
  return body.unread
}

export async function archiveEbuyOpportunity(
  orgId: string,
  opportunityId: string,
  accessToken: string,
  is_archived: boolean,
): Promise<void> {
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/gsa-ebuy/opportunities/${opportunityId}/archive`,
    {
      method: 'PATCH',
      headers: authHeaders(accessToken),
      body: JSON.stringify({ archived: is_archived }),
    },
  )
  await checkOk(res)
}

export async function markEbuyOpportunityRead(
  orgId: string,
  opportunityId: string,
  accessToken: string,
  is_read: boolean,
): Promise<void> {
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/gsa-ebuy/opportunities/${opportunityId}/read`,
    {
      method: 'PATCH',
      headers: authHeaders(accessToken),
      body: JSON.stringify({ is_read }),
    },
  )
  await checkOk(res)
}

export async function createPursuitFromOpportunity(
  orgId: string,
  opportunityId: string,
  accessToken: string,
  title?: string | null,
): Promise<UUID> {
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/gsa-ebuy/opportunities/${opportunityId}/pursuit`,
    {
      method: 'POST',
      headers: authHeaders(accessToken),
      body: JSON.stringify({ title: title ?? null }),
    },
  )
  await checkOk(res)
  const body = (await res.json()) as ApiPursuitCreated
  return body.pursuit_id as UUID
}

// ─── Tracked SINs ─────────────────────────────────────────────────────────────

interface ApiTrackedSins {
  sins: string[]
}

export async function fetchTrackedSins(
  orgId: string,
  accessToken: string,
): Promise<string[]> {
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/gsa-ebuy/tracked-sins`,
    { headers: authHeaders(accessToken) },
  )
  await checkOk(res)
  const body = (await res.json()) as ApiTrackedSins
  return body.sins
}

export async function addTrackedSins(
  orgId: string,
  accessToken: string,
  sins: string[],
): Promise<string[]> {
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/gsa-ebuy/tracked-sins`,
    {
      method: 'POST',
      headers: authHeaders(accessToken),
      body: JSON.stringify({ sins }),
    },
  )
  await checkOk(res)
  const body = (await res.json()) as ApiTrackedSins
  return body.sins
}

export async function removeTrackedSin(
  orgId: string,
  accessToken: string,
  sin: string,
): Promise<void> {
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/gsa-ebuy/tracked-sins/${encodeURIComponent(sin)}`,
    {
      method: 'DELETE',
      headers: authHeaders(accessToken),
    },
  )
  await checkOk(res)
}

export async function fetchEbuyAttachment(
  orgId: string,
  attachmentId: string,
  accessToken: string,
): Promise<EbuyAttachment> {
  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/gsa-ebuy/attachments/${attachmentId}`,
    { headers: authHeaders(accessToken) },
  )
  await checkOk(res)
  const a = (await res.json()) as ApiAttachment
  return mapAttachment(a)
}
